"use client";

import { useTranslations } from "next-intl";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { MotionWrapper } from "@/components/ui/motion-wrapper";

const marketData = [
  { year: "2019", value: 64.02 },
  { year: "2020", value: 50.1 },
  { year: "2021", value: 65.0 },
  { year: "2022", value: 67.4 },
  { year: "2023", value: 65.0 },
  { year: "2024", value: 73.4 },
  { year: "2030", value: 85.2 },
];

export function MarketGrowthSection() {
  const t = useTranslations("marketGrowth");

  return (
    <section className="py-16 md:py-32 px-4 md:px-8 bg-white">
      <div className="max-w-5xl xl:max-w-6xl 2xl:max-w-7xl mx-auto">
        <MotionWrapper variant="slideUp" delay={0} duration={0.5}>
          <div className="text-center mb-12 md:mb-20">
            <h2 className="text-sm uppercase tracking-[0.3em] text-brand-blue mb-4 md:mb-8">
              {t("subtitle")}
            </h2>
            <h3 className="text-3xl md:text-5xl lg:text-6xl font-light leading-tight mb-6 md:mb-12">
              {t("title")}
              <br />
              <strong className="text-brand-red italic font-semibold">
                {t("titleHighlight")}
              </strong>
            </h3>
            <div className="space-y-4 md:space-y-6 max-w-4xl mx-auto">
              <p className="text-base md:text-xl text-gray-600 font-medium leading-relaxed">
                {t("description1")}
              </p>
              <p className="text-base md:text-xl text-gray-600 font-medium leading-relaxed">
                {t("description2")}
              </p>
            </div>
          </div>
        </MotionWrapper>

        <MotionWrapper variant="slideUp" delay={0.1} duration={0.5}>
          <div className="p-4 md:p-8">
            <div className="mb-8">
              <h4 className="text-lg md:text-2xl font-semibold text-gray-800 mb-2 md:mb-4">
                {t("chart.title")}
              </h4>
            </div>

            <div className="relative h-64 md:h-96 w-full">
              <ResponsiveContainer
                width="100%"
                height="100%"
                initialDimension={{ width: 600, height: 200 }}
              >
                <LineChart
                  data={marketData}
                  margin={{ top: 10, right: 10, bottom: 10, left: 10 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    dataKey="year"
                    stroke="#6b7280"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    stroke="#6b7280"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                    label={{
                      value: t("chart.yAxisLabel"),
                      angle: -90,
                      position: "insideLeft",
                      style: { textAnchor: "middle", fontSize: 14 },
                    }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "white",
                      border: "1px solid #e5e7eb",
                      borderRadius: "8px",
                      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                    }}
                    formatter={(value: number) => [
                      `$${value}B`,
                      t("chart.tooltipLabel"),
                    ]}
                    labelFormatter={(label) =>
                      `${t("chart.yearLabel")}: ${label}`
                    }
                  />
                  <Legend />
                  <Line
                    type="linear"
                    dataKey="value"
                    stroke="#d3081f"
                    strokeWidth={3}
                    dot={{ fill: "#d3081f", strokeWidth: 2, r: 6 }}
                    activeDot={{ r: 8, stroke: "#d3081f", strokeWidth: 2 }}
                    name={t("chart.lineLabel")}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <MotionWrapper variant="fadeIn" delay={0.2} duration={0.4}>
              <div className="mt-4 md:mt-6 text-sm text-gray-500">
                <p>
                  {t("chart.sourceLabel")}:{" "}
                  <a
                    href="https://www.statista.com/topics/1119/art-market/"
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="text-brand-blue hover:underline"
                  >
                    Statista
                  </a>
                  ,{" "}
                  <a
                    href="https://www.expertmarketresearch.com/reports/art-market"
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="text-brand-blue hover:underline"
                  >
                    Expert Market Research
                  </a>
                </p>
              </div>
            </MotionWrapper>
          </div>
        </MotionWrapper>
      </div>
    </section>
  );
}
